import React from 'react'
import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import {
  Truck,
  Warehouse,
  Target,
  Users,
  ChartNoAxesCombined,
  Network,
  ArrowRight,
  Boxes,
  CheckCircle2,
  Headset,
  Settings,
  MoveRight,
  ClipboardCheck
} from 'lucide-react'

import ContractLogisticsImage from '../assets/images/data/contract-logistics.png'
import CTA from './CTA'
import IndustryExpertise from './IndustryExpertise'
import MoreServicesSection from './MoreServicesSection'

const solutions = [
  {
    number: '01',
    icon: Target,
    title: 'Dedicated KPI & SLA Management',
    description:
      'Clearly defined performance indicators, service-level commitments and regular reviews to support consistent logistics operations.'
  },
  {
    number: '02',
    icon: Truck,
    title: 'Customized Fleet Allocation',
    description:
      'Dedicated vehicle planning and fleet allocation aligned with your shipment volumes, routes and operational requirements.'
  },
  {
    number: '03',
    icon: Warehouse,
    title: 'Customized Warehouse Setup',
    description:
      'Warehouse planning and operational support designed around your inventory, storage and distribution needs.'
  },
  {
    number: '04',
    icon: Users,
    title: 'Dedicated Key Account Manager',
    description:
      'A single point of contact for coordination, communication, performance reviews and ongoing operational support.'
  },
  {
    number: '05',
    icon: Network,
    title: 'Supply Chain Network Design',
    description:
      'Strategic logistics planning to connect suppliers, warehouses, distribution centres and customer destinations.'
  },
  {
    number: '06',
    icon: Settings,
    title: 'Turnkey 3PL Operations',
    description:
      'Integrated third-party logistics support covering transportation, warehousing and day-to-day operational coordination.'
  }
]

const advantages = [
  {
    icon: ChartNoAxesCombined,
    title: 'Performance-Driven Operations',
    description:
      'Structured KPI monitoring and SLA reviews help maintain operational visibility and service consistency.'
  },
  {
    icon: Truck,
    title: 'Dedicated Logistics Resources',
    description:
      'Fleet and operational resources can be planned around your business requirements.'
  },
  {
    icon: Warehouse,
    title: 'Scalable Infrastructure',
    description:
      'Flexible warehouse and distribution planning that can adapt as your business grows.'
  },
  {
    icon: Headset,
    title: 'Account Management Support',
    description:
      'Dedicated coordination for operational communication, issue resolution and continuous improvement.'
  }
]

const process = [
  {
    number: '01',
    icon: ClipboardCheck,
    title: 'Understand Your Business',
    description:
      'We evaluate your supply chain, shipment patterns, operational challenges and business goals.'
  },
  {
    number: '02',
    icon: Target,
    title: 'Design the Solution',
    description:
      'We develop a customized logistics model covering fleet, warehousing, routes and service expectations.'
  },
  {
    number: '03',
    icon: Settings,
    title: 'Implement Operations',
    description:
      'We coordinate resources, operational workflows and service processes for the agreed logistics setup.'
  },
  {
    number: '04',
    icon: ChartNoAxesCombined,
    title: 'Monitor & Optimize',
    description:
      'Ongoing KPI reviews, SLA monitoring and operational improvements help keep your supply chain aligned.'
  }
]

const capabilities = [
  {
    icon: Truck,
    title: 'Dedicated Transportation',
    description:
      'Fleet planning and transportation resources aligned with your operational needs.'
  },
  {
    icon: Warehouse,
    title: 'Warehouse Management',
    description: 'Customized warehouse planning and storage operations.'
  },
  {
    icon: Network,
    title: 'Distribution Operations',
    description: 'Coordinated distribution support across your supply chain.'
  },
  {
    icon: Boxes,
    title: 'Inventory Coordination',
    description: 'Organized inventory handling and operational coordination.'
  },
  {
    icon: Settings,
    title: 'Fleet Planning',
    description:
      'Vehicle allocation and route planning for business requirements.'
  },
  {
    icon: ChartNoAxesCombined,
    title: 'Supply Chain Optimization',
    description: 'Performance reviews and operational improvements.'
  }
]

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
}

const sectionAnimation = {
  initial: 'hidden',
  whileInView: 'visible',
  viewport: { once: true, amount: 0.15 },
  variants: fadeUp,
  transition: { duration: 0.6 }
}

export default function ContractLogisticsPage () {
  return (
    <>
      <Helmet>
        <title>
          Contract Logistics & Enterprise 3PL Solutions | S Infinite Express
        </title>

        <meta
          name='description'
          content='S Infinite Express provides tailored enterprise contract logistics, dedicated fleet allocation, customized warehouse setup, KPI and SLA management, and turnkey 3PL operations.'
        />

        <meta
          name='keywords'
          content='Contract Logistics, Enterprise Logistics, Third Party Logistics, 3PL Services, Dedicated Fleet, Warehouse Management, Supply Chain Management, S Infinite Express'
        />

        <meta name='robots' content='index, follow' />

        <link
          rel='canonical'
          href='https://sinfiniteexpress.com/services/contract-logistics'
        />

        <meta
          property='og:title'
          content='Contract Logistics | S Infinite Express'
        />

        <meta
          property='og:description'
          content='Tailored long-term logistics solutions, dedicated fleet and warehouse setup, and enterprise 3PL operations.'
        />

        <meta
          property='og:url'
          content='https://sinfiniteexpress.com/services/contract-logistics'
        />

        <meta property='og:type' content='website' />

        <meta
          property='og:image'
          content='https://sinfiniteexpress.com/static/media/contract-logistics.png'
        />

        <meta property='og:site_name' content='S Infinite Express' />
      </Helmet>

      <main className='overflow-hidden bg-white'>
        {/* HERO SECTION */}

        <section className='relative bg-gradient-to-br from-white via-blue-50/40 to-white overflow-hidden'>
          <div className='max-w-7xl mx-auto px-5 sm:px-8 lg:px-6 py-8 md:py-12'>
            <div className='grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center'>
              {/* LEFT CONTENT */}

              <motion.div {...sectionAnimation} className='space-y-6'>
                <h1 className='text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-extrabold text-[#211f57] leading-tight'>
                  Contract
                  <span className='text-[#d32c30]'> Logistics</span>
                </h1>

                <div className='w-24 h-1.5 bg-[#d32c30] rounded-full' />

                <h2 className='text-xl sm:text-2xl font-bold text-[#211f57]'>
                  Your Supply Chain. Strategically Managed.
                </h2>

                <p className='text-gray-600 text-base sm:text-lg leading-relaxed'>
                  <span className='font-bold text-[#d32c30]'>
                    S INFINITE EXPRESS
                  </span>{' '}
                  provides customized long-term logistics partnerships with
                  dedicated fleet allocation, tailored warehouse infrastructure
                  and integrated 3PL operations designed around your business.
                </p>

                <p className='text-gray-600 leading-relaxed'>
                  From transportation and warehousing to distribution and
                  operational coordination, we help businesses build logistics
                  solutions aligned with their supply chain requirements.
                </p>

                {/* ACTION BUTTONS */}

                <div className='flex flex-col sm:flex-row gap-4 pt-3'>
                  <a
                    href='/contact-us'
                    className='inline-flex items-center justify-center gap-3 px-7 py-4 bg-[#d32c30] text-white font-semibold rounded-xl shadow-lg hover:bg-[#b82629] hover:-translate-y-1 transition-all duration-300'
                  >
                    Discuss Your Requirements
                    <MoveRight className='w-5 h-5' />
                  </a>

                  <a
                    href='#contract-solutions'
                    className='inline-flex items-center justify-center gap-3 px-7 py-4 border-2 border-[#211f57] text-[#211f57] font-semibold rounded-xl hover:bg-[#211f57] hover:text-white transition-all duration-300'
                  >
                    Explore Solutions
                    <ArrowRight className='w-5 h-5' />
                  </a>
                </div>

                {/* TRUST POINTS */}

                <div className='flex flex-wrap gap-x-6 gap-y-3 pt-5 text-sm font-medium text-gray-600'>
                  {[
                    'Dedicated Resources',
                    'Customized Operations',
                    'Performance Monitoring'
                  ].map(item => (
                    <span key={item} className='flex items-center gap-2'>
                      <CheckCircle2 className='w-4 h-4 text-green-600 shrink-0' />
                      {item}
                    </span>
                  ))}
                </div>
              </motion.div>

              {/* RIGHT IMAGE */}

              <motion.div
                initial={{ opacity: 0, scale: 0.94, x: 30 }}
                whileInView={{ opacity: 1, scale: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className='relative'
              >
                <div className='absolute -top-6 -right-5 w-32 h-32 bg-[#fff44f]/60 rounded-full blur-3xl' />

                <div className='absolute -bottom-6 -left-5 w-32 h-32 bg-blue-200/70 rounded-full blur-3xl' />

                <div className='relative p-2 bg-white rounded-3xl shadow-2xl border border-gray-100'>
                  <img
                    src={ContractLogisticsImage}
                    alt='S Infinite Express enterprise contract logistics and warehouse operations'
                    className='w-full h-[200px] sm:h-[300px] lg:h-[420px] object-cover rounded-2xl'
                    loading='lazy'
                    decoding='async'
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* INTRODUCTION SECTION */}

        <section className='relative overflow-hidden bg-gradient-to-br from-yellow-50 via-blue-50/40 to-yellow-100/60 py-8 sm:py-10 lg:py-12'>
          <div className='absolute -top-20 -left-20 h-64 w-64 rounded-full bg-[#fff44f]/10 blur-[90px]' />

          <div className='absolute -bottom-20 -right-20 h-64 w-64 rounded-full bg-blue-200/30 blur-[90px]' />

          <div className='relative mx-auto max-w-7xl px-5 sm:px-8 lg:px-12'>
            <div className='grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16'>
              {/* LEFT CONTENT */}

              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                className='space-y-6'
              >
                <h2 className='text-2xl font-bold leading-tight text-[#211f57] sm:text-2xl lg:text-3xl'>
                  Logistics Designed Around Your
                  <span className='text-[#d32c30]'>
                    {' '}
                    Enterprise Requirements
                  </span>
                </h2>

                <p className='text-base leading-relaxed text-gray-600 sm:text-lg'>
                  At{' '}
                  <span className='font-bold text-[#d32c30]'>
                    S INFINITE EXPRESS
                  </span>
                  , we understand that every enterprise has unique supply chain
                  requirements. Our contract logistics approach brings
                  transportation, warehousing, distribution and operational
                  management together under a customized logistics partnership.
                </p>

                <p className='leading-relaxed text-gray-600'>
                  We work with businesses to plan dedicated logistics resources,
                  build operational workflows and coordinate 3PL activities
                  aligned with their business objectives.
                </p>

                <div className='grid grid-cols-1 gap-x-5 gap-y-4 pt-3 sm:grid-cols-2'>
                  {[
                    'Long-Term Partnerships',
                    'Dedicated Resources',
                    'Customized Operations',
                    'Performance Monitoring'
                  ].map((item, index) => (
                    <div key={index} className='flex items-center gap-3'>
                      <CheckCircle2
                        size={20}
                        className='shrink-0 text-green-600'
                      />

                      <span className='text-sm font-semibold text-[#211f57]'>
                        {item}
                      </span>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* RIGHT CAPABILITIES CARD */}

              <motion.div
                initial={{ opacity: 0, scale: 0.94, x: 30 }}
                whileInView={{ opacity: 1, scale: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className='relative'
              >
                <div className='absolute -right-5 -top-6 h-32 w-32 rounded-full bg-[#fff44f]/50 blur-3xl' />

                <div className='absolute -bottom-6 -left-5 h-32 w-32 rounded-full bg-blue-200/70 blur-3xl' />

                <div className='relative overflow-hidden rounded-3xl bg-[#211f57] p-3 shadow-2xl sm:p-4 lg:p-5'>
                  <div className='absolute -right-16 -top-16 h-48 w-48 rounded-full border-[25px] border-white/5' />

                  <div className='absolute -bottom-20 -right-10 h-56 w-56 rounded-full border-[25px] border-white/5' />

                  <div className='relative z-10'>
                    <span className='text-xs font-bold uppercase tracking-[2px] text-[#fff44f]'>
                      Our Capabilities
                    </span>

                    <p className='mb-2 mt-3 text-sm leading-7 text-white/70 sm:text-base'>
                      A connected logistics ecosystem bringing essential supply
                      chain functions together through dedicated resources and
                      coordinated operations.
                    </p>

                    <div className='space-y-2'>
                      {capabilities.slice(0, 4).map(item => (
                        <div
                          key={item.title}
                          className='flex items-center gap-4 rounded-xl border border-white/10 bg-white/5 p-4 transition-all duration-300 hover:bg-white/10'
                        >
                          <div>
                            <h4 className='text-sm font-bold text-white sm:text-base'>
                              {item.title}
                            </h4>

                            <p className='mt-1 text-xs leading-5 text-white/55 sm:text-sm'>
                              {item.description}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* CONTRACT LOGISTICS SOLUTIONS */}

        <section
          id='contract-solutions'
          className='py-5 bg-[#f7f8fc] scroll-mt-24'
        >
          <div className='max-w-7xl mx-auto px-6'>
            <div className='max-w-4xl mx-auto text-center mb-5'>
              <span className='text-[#d32c30] font-extrabold text-xs uppercase tracking-[2px]'>
                Our Enterprise Solutions
              </span>

              <h2 className='text-3xl sm:text-4xl font-extrabold text-[#211f57]'>
                Our Contract Logistics Solutions
              </h2>

              <p className='text-gray-500 leading-7'>
                Customized logistics solutions designed around your
                infrastructure, shipment volumes and operational requirements.
              </p>
            </div>

            <div className='grid sm:grid-cols-2 lg:grid-cols-3 gap-6'>
              {solutions.map((solution, index) => {
                const Icon = solution.icon

                return (
                  <motion.div
                    key={solution.number}
                    initial={{ opacity: 0, y: 25 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{
                      duration: 0.5,
                      delay: index * 0.08
                    }}
                    className='group bg-white p-7 rounded-2xl border border-gray-100 hover:border-[#d32c30]/30 hover:shadow-xl transition-all duration-300'
                  >
                    <div className='flex justify-between items-center'>
                      <div className='w-14 h-14 rounded-xl bg-[#211f57] group-hover:bg-[#d32c30] flex items-center justify-center text-white transition-colors'>
                        <Icon size={26} />
                      </div>

                      <span className='text-3xl font-black text-gray-100 group-hover:text-[#d32c30]/20 transition-colors'>
                        {solution.number}
                      </span>
                    </div>

                    <h3 className='text-lg font-extrabold text-[#211f57] mt-3'>
                      {solution.title}
                    </h3>

                    <p className='text-sm text-gray-500 leading-7 mt-2'>
                      {solution.description}
                    </p>
                  </motion.div>
                )
              })}
            </div>
          </div>
        </section>

        {/* INDUSTRY EXPERTISE */}

        <IndustryExpertise />

        {/* WHY CHOOSE US */}

        <section className='relative overflow-hidden bg-gradient-to-br from-yellow-50 via-blue-50/40 to-yellow-100/60 py-6 sm:py-8 lg:py-10'>
          <div className='absolute -top-20 -left-20 h-64 w-64 rounded-full bg-[#fff44f]/10 blur-[90px]' />

          <div className='absolute -bottom-20 -right-20 h-64 w-64 rounded-full bg-blue-200/30 blur-[90px]' />

          <div className='relative max-w-7xl mx-auto px-6'>
            <div className='text-center max-w-4xl mx-auto mb-5'>
              <span className='text-[#d32c30] font-extrabold uppercase tracking-[2px] text-xs'>
                The S Infinite Advantage
              </span>

              <h2 className='text-3xl sm:text-4xl font-extrabold text-[#211f57]'>
                Why Choose Our Contract Logistics?
              </h2>

              <p className='text-gray-500 leading-7'>
                Reliable logistics partnerships backed by dedicated resources,
                scalable infrastructure and efficient operations.
              </p>
            </div>

            <div className='grid sm:grid-cols-2 lg:grid-cols-4 gap-6'>
              {advantages.map((item, index) => {
                const Icon = item.icon

                return (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 25 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{
                      duration: 0.5,
                      delay: index * 0.08
                    }}
                    className='group bg-white p-7 rounded-2xl border border-gray-100 hover:border-[#d32c30]/30 hover:shadow-xl transition-all duration-300'
                  >
                    <div className='w-14 h-14 rounded-xl bg-[#211f57] group-hover:bg-[#d32c30] flex items-center justify-center text-white transition-colors'>
                      <Icon size={26} />
                    </div>

                    <h3 className='text-lg font-extrabold text-[#211f57] mt-4'>
                      {item.title}
                    </h3>

                    <p className='text-sm text-gray-500 leading-7 mt-2'>
                      {item.description}
                    </p>
                  </motion.div>
                )
              })}
            </div>
          </div>
        </section>

        {/* OPERATIONAL CAPABILITIES */}

        <section className='py-8 sm:py-10 lg:py-12 bg-white'>
          <div className='max-w-7xl mx-auto px-6'>
            <div className='grid grid-cols-1 lg:grid-cols-2 gap-12 items-center'>
              <motion.div {...sectionAnimation}>
                <span className='text-[#d32c30] font-extrabold text-xs uppercase tracking-[2px]'>
                  Integrated Capabilities
                </span>

                <h2 className='text-2xl sm:text-2xl font-extrabold text-[#211f57] mt-2 leading-tight'>
                  One Partner.
                  <span className='text-[#d32c30]'>
                    {' '}
                    Multiple Logistics Functions.
                  </span>
                </h2>

                <p className='text-gray-600 leading-8 mt-5'>
                  Bring essential supply chain activities together through an
                  integrated logistics model tailored to your business. Our
                  capabilities support transportation, warehousing, distribution
                  and operational coordination.
                </p>

                <p className='text-gray-600 leading-8 mt-3'>
                  Whether you need dedicated fleet planning, inventory
                  coordination or broader supply chain support, our approach
                  focuses on aligning logistics operations with your goals.
                </p>

                <a
                  href='/contact-us'
                  className='inline-flex items-center gap-3 mt-7 px-6 py-4 bg-[#d32c30] text-white text-sm font-bold rounded-xl shadow-lg hover:bg-[#b82629] hover:-translate-y-1 transition-all duration-300'
                >
                  Discuss Your Logistics Needs
                  <ArrowRight size={17} />
                </a>
              </motion.div>

              <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                {capabilities.map((item, index) => {
                  const Icon = item.icon

                  return (
                    <motion.div
                      key={item.title}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{
                        duration: 0.4,
                        delay: index * 0.07
                      }}
                      className='group bg-[#f7f8fc] border border-gray-100 rounded-2xl p-6 hover:border-[#d32c30]/30 hover:shadow-lg transition-all duration-300'
                    >
                      <div className='w-12 h-12 rounded-xl bg-[#211f57] group-hover:bg-[#d32c30] text-white flex items-center justify-center transition-colors'>
                        <Icon size={23} />
                      </div>

                      <h3 className='text-base font-extrabold text-[#211f57] mt-4'>
                        {item.title}
                      </h3>

                      <p className='text-sm text-gray-500 leading-6 mt-2'>
                        {item.description}
                      </p>
                    </motion.div>
                  )
                })}
              </div>
            </div>
          </div>
        </section>

        {/* PROCESS SECTION */}

        <section className='py-5 lg:py-6 bg-[#f7f8fc]'>
          <div className='max-w-7xl mx-auto px-6'>
            <div className='text-center max-w-4xl mx-auto mb-4'>
              <span className='text-[#d32c30] font-extrabold text-xs uppercase tracking-[2px]'>
                Our Approach
              </span>

              <h2 className='text-2xl sm:text-3xl font-extrabold text-[#211f57]'>
                From Strategy to Execution
              </h2>

              <p className='text-gray-500 leading-7'>
                A structured approach to building, implementing and managing
                your enterprise logistics operations.
              </p>
            </div>

            <div className='grid sm:grid-cols-2 lg:grid-cols-4 gap-8 relative'>
              {process.map((step, index) => {
                const Icon = step.icon

                return (
                  <motion.div
                    key={step.number}
                    initial={{ opacity: 0, y: 25 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{
                      duration: 0.5,
                      delay: index * 0.1
                    }}
                    className='relative text-center'
                  >
                    <div className='w-16 h-16 mx-auto rounded-2xl bg-[#211f57] text-white flex items-center justify-center shadow-lg'>
                      <Icon size={27} />
                    </div>

                    <span className='inline-block text-xs font-extrabold text-[#d32c30] mt-4'>
                      STEP {step.number}
                    </span>

                    <h3 className='text-lg font-extrabold text-[#211f57] mt-2'>
                      {step.title}
                    </h3>

                    <p className='text-sm text-gray-500 leading-7 mt-3'>
                      {step.description}
                    </p>

                    {index !== process.length - 1 && (
                      <div className='hidden lg:block absolute top-8 left-[calc(50%+45px)] w-[calc(100%-50px)] border-t-2 border-dashed border-[#d32c30]/30' />
                    )}
                  </motion.div>
                )
              })}
            </div>
          </div>
        </section>

        {/* FINAL CTA */}

        <section className='relative bg-[#211f57] py-2 overflow-hidden'>
          <div className='absolute right-0 top-0 w-96 h-96 rounded-full border-[50px] border-white/5 translate-x-1/3 -translate-y-1/3' />

          <div className='relative max-w-5xl mx-auto px-6 text-center'>
            <h2 className='text-2xl sm:text-3xl lg:text-3xl font-extrabold text-white mt-2 leading-tight'>
              Ready for Logistics Built Around Your Business?
              <span className='block text-[#FFF44F]'>
                Let's Grow Your Supply Chain Together.
              </span>
            </h2>

            <p className='text-white/70 max-w-4xl mx-auto mt-4 leading-8'>
              Connect with S Infinite Express for customized logistics,
              dedicated fleets, warehousing and 3PL solutions.
            </p>
          </div>
        </section>

        {/* MORE SERVICES */}

        <MoreServicesSection />

        {/* COMMON CTA */}

        <CTA />
      </main>
    </>
  )
}
